import React from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { createStyles, palette, spacing } from "../../../styles/theme";
import { importImageFiles } from "./importImageFile";
import type { ImportImageInput } from "./types";

type ImageDropZoneProps = {
  label: string;
  value: ImportImageInput | null;
  onChange: (input: ImportImageInput) => void;
};

const styles = createStyles({
  dropZone: {
    width: "100%",
    p: spacing.groupingDesktop,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    borderRadius: "4px",
    transition: "background 0.2s",
    "&:hover": {
      bgcolor: palette.brand.paleEffect,
    },
  },
});

export default function ImageDropZone({
  label,
  value,
  onChange,
}: ImageDropZoneProps) {
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setPreviewUrl(value?.compositeDataUrl ?? null);
  }, [value]);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const input = await importImageFiles(files);
      setPreviewUrl(input.compositeDataUrl);
      onChange(input);
    } catch (caughtError) {
      setError(
        caughtError instanceof Error ? caughtError.message : "Upload failed"
      );
    } finally {
      setLoading(false);
      event.target.value = "";
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box component="label" sx={styles.dropZone}>
        <input
          type="file"
          accept=".psd,.png,.jpg,.jpeg,.webp,.tiff,.tif"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        {loading ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          </Stack>
        ) : previewUrl ? (
          <Box
            component="img"
            src={previewUrl}
            alt={label}
            sx={{ maxWidth: "100%", maxHeight: 280, objectFit: "contain" }}
          />
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1.5}
            sx={{
              py: 5,
              px: 4,
              border: "2px dashed #c0c5cd",
              borderRadius: "8px",
              width: "100%",
              minHeight: 200,
              textAlign: "center",
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: "#d0d5dd" }} />
            <Typography variant="body2" color="text.secondary">
              Upload {label.toLowerCase()} image
            </Typography>
            <Typography variant="caption" sx={{ color: "#98a2b3" }}>
              PSD, PNG, JPG, TIFF, or WEBP.
            </Typography>
          </Stack>
        )}
      </Box>

      {value ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: "block", textAlign: "center" }}
        >
          {value.fileName}
        </Typography>
      ) : null}

      {error ? (
        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}
